import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TabBar } from '../components/TabBar';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, Kennzahl } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { IconPfeilLinks, IconPlus, IconZaehler } from '../components/icons';
import { cn } from '../lib/cn';
import { DEMO, api, ApiFehler } from '../lib/api';
import { beschreibeSaldo, formatDatum, formatEuro, formatQm, formatZahl, formatZeitraum, parseZahlEingabe } from '../lib/format';
import { transition, uebergangSanft } from '../lib/motion';
import {
  type AkteAntwort,
  type Einheit,
  type Mietverhaeltnis,
  type ZaehlerMitAblesungen,
  DACHGESCHOSS,
  GRUND_LABEL,
  dauer,
  demoAkte,
  demoHaus,
  demoHausSpeichern,
  kennzahlenAus,
  etagen,
  geschossLang,
  heuteIso,
  tagDanach,
  verbrauchszeitraeume,
  werteAm,
} from '../lib/haus';
import {
  KAUTION_LABEL,
  type KautionStatus,
  KommunikationPanel,
  type LeaseV,
  LeaseFormular,
  MIETART_LABEL,
  type MieterV,
  MieterhoehungPanel,
  UebergabePanel,
  ZahlungenPanel,
  kautionTon,
} from './Mietverhaeltnis';
import { ANLASS_LABEL, AblesungFormular, MEDIUM_META, ZaehlerFormular } from './Zaehler';
import { MieterKontaktPanel, VertragPanel } from './EinheitVertrag';
import { Bestaetigung } from '../components/Bestaetigung';

/**
 * Einheitenakte: alles zu einer Wohnung an einem Ort — aktuelles Mietverhältnis mit allen
 * Vorgängen, frühere Mieter, Zähler und Verbräuche, Abrechnungsergebnisse je Jahr.
 */

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

type Reiter = 'mietverhaeltnis' | 'verlauf' | 'verbrauch' | 'abrechnungen';

export interface EinheitAkteProps {
  einheitId: string;
  onZurueck: () => void;
  /** Nach Änderungen (Auszug, neues Mietverhältnis, Stammdaten): Hausansicht neu laden. */
  onGeaendert: () => void;
  /** Einheit gelöscht: zurück zum Haus mit Meldung. */
  onEntfernt?: (meldung: string) => void;
  onOeffneAbrechnungen?: () => void;
}

/** Lease-Sicht für die Vorgangs-Panels — mit den heute gültigen Vertragswerten. */
function alsLease(m: Mietverhaeltnis, einheit: string): LeaseV {
  const w = werteAm(m, heuteIso());
  return {
    id: m.id,
    einheitId: m.einheitId,
    einheit,
    mieter: m.mieterName,
    beginn: m.beginn,
    ende: m.ende,
    kaltmieteCent: w.kaltmieteCent,
    vzNkCent: w.vzNebenkostenCent,
    vzHeizCent: w.vzHeizkostenCent,
    personen: w.personenzahl,
    mietart: m.mietart ?? 'normal',
    letzteErhoehungAm: m.letzteErhoehungAm,
    kautionCent: m.kautionCent ?? 0,
    kautionStatus: m.kautionStatus ?? 'offen',
  };
}

export function EinheitAkte({ einheitId, onZurueck, onGeaendert, onEntfernt, onOeffneAbrechnungen }: EinheitAkteProps) {
  const reduziert = useReducedMotion();
  const [akte, setAkte] = useState<AkteAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [reiter, setReiter] = useState<Reiter>('mietverhaeltnis');
  const [bearbeiten, setBearbeiten] = useState(false);

  async function laden() {
    if (DEMO) {
      setAkte((a) => a ?? demoAkte(einheitId));
      return;
    }
    try {
      setAkte(await api.get<AkteAntwort>(`/einheiten/${einheitId}/akte`));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Einheit konnte nicht geladen werden.');
    }
  }

  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [einheitId]);

  function geaendert() {
    void laden();
    onGeaendert();
  }

  if (!akte) {
    return (
      <div>
        <ZurueckKnopf onClick={onZurueck} text="Zurück zum Haus" />
        {fehler ? (
          <Card>
            <p className="text-sm text-danger">{fehler}</p>
          </Card>
        ) : (
          <p className="text-sm text-fg-muted">Wird geladen …</p>
        )}
      </div>
    );
  }

  const { einheit, objekt } = akte;
  const heute = heuteIso();
  const aktiv = akte.mietverhaeltnisse.find((m) => m.beginn <= heute && (m.ende === null || m.ende >= heute)) ?? null;
  const kuenftig = akte.mietverhaeltnisse.filter((m) => m.beginn > heute).sort((a, b) => a.beginn.localeCompare(b.beginn))[0] ?? null;
  const status = aktiv ? 'vermietet' : einheit.eigennutzung ? 'eigennutzung' : 'leer';
  const meta = [
    geschossLang(einheit.geschoss),
    einheit.position,
    formatQm(einheit.wohnflaecheQm),
    einheit.zimmer ? `${formatZahl(einheit.zimmer)} Zimmer` : null,
    einheit.nutzungsart === 'gewerbe' ? 'Gewerbe' : null,
  ].filter(Boolean);

  const reiterListe = [
    { id: 'mietverhaeltnis', label: 'Mietverhältnis' },
    { id: 'verlauf', label: 'Historie' },
    { id: 'verbrauch', label: 'Verbrauch' },
    { id: 'abrechnungen', label: 'Abrechnungen' },
  ];

  return (
    <div>
      <ZurueckKnopf onClick={onZurueck} text={objekt.bezeichnung} />

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-fg">{einheit.bezeichnung}</h1>
            <Badge ton={status === 'vermietet' ? 'erfolg' : status === 'leer' ? 'warnung' : 'info'} punkt>
              {status === 'vermietet' ? 'Vermietet' : status === 'leer' ? 'Leerstand' : 'Eigennutzung'}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-fg-muted">{meta.join(' · ')}</p>
        </div>
        <Button variante="sekundaer" groesse="sm" onClick={() => setBearbeiten((b) => !b)}>
          {bearbeiten ? 'Schließen' : 'Einheit bearbeiten'}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {bearbeiten && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition(!!reduziert, uebergangSanft)}
            className="overflow-hidden"
          >
            <EinheitBearbeiten
              einheit={einheit}
              struktur={objekt}
              hatMietverhaeltnisse={akte.mietverhaeltnisse.length > 0}
              onGeloescht={() => (onEntfernt ? onEntfernt(`${einheit.bezeichnung} wurde gelöscht.`) : onZurueck())}
              onGespeichert={(neu) => {
                setAkte((a) => (a ? { ...a, einheit: neu } : a));
                setBearbeiten(false);
                onGeaendert();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mb-4 overflow-x-auto">
        <TabBar
          tabs={reiterListe}
          aktiv={reiter}
          onWechsel={(id) => setReiter(id as Reiter)}
          ariaLabel="Bereiche der Einheit"
          panelId="einheit-inhalt"
        />
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={reiter}
          id="einheit-inhalt"
          role="tabpanel"
          initial={reduziert ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduziert ? undefined : { opacity: 0, y: -4 }}
          transition={transition(!!reduziert, uebergangSanft)}
        >
          {reiter === 'mietverhaeltnis' && (
            <MietverhaeltnisBereich
              akte={akte}
              aktiv={aktiv}
              kuenftig={kuenftig}
              onGeaendert={geaendert}
              onVorschau={(f) => setAkte((a) => (a ? f(a) : a))}
            />
          )}
          {reiter === 'verlauf' && <Verlauf mvs={akte.mietverhaeltnisse} einheit={einheit} />}
          {reiter === 'verbrauch' && <Verbrauch akte={akte} onGeaendert={geaendert} />}
          {reiter === 'abrechnungen' && <Abrechnungen akte={akte} onOeffneAbrechnungen={onOeffneAbrechnungen} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ZurueckKnopf({ onClick, text }: { onClick: () => void; text: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-3 inline-flex items-center gap-1 rounded-md text-sm font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <IconPfeilLinks groesse={16} /> {text}
    </button>
  );
}

/* ------------------------------------------------------------------ Mietverhältnis */

type Panel = 'auszug' | 'vertrag' | 'kontakt' | 'erhoehung' | 'zahlungen' | 'uebergabe' | 'verlauf' | null;

function MietverhaeltnisBereich({
  akte,
  aktiv,
  kuenftig,
  onGeaendert,
  onVorschau,
}: {
  akte: AkteAntwort;
  aktiv: Mietverhaeltnis | null;
  kuenftig: Mietverhaeltnis | null;
  onGeaendert: () => void;
  /** Nur Vorschau (ohne Server): Änderung lokal in der Akte nachführen. */
  onVorschau: (f: (a: AkteAntwort) => AkteAntwort) => void;
}) {
  const { einheit, objekt } = akte;
  const [panel, setPanel] = useState<Panel>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [vorgabeBeginn, setVorgabeBeginn] = useState<string | undefined>(undefined);
  const [mieter, setMieter] = useState<MieterV[]>([]);
  const [kaution, setKaution] = useState<KautionStatus | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [loeschen, setLoeschen] = useState<Mietverhaeltnis | null>(null);
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  const [loeschtGerade, setLoeschtGerade] = useState(false);

  async function mvLoeschen() {
    if (!loeschen) return;
    setLoeschtGerade(true);
    setLoeschFehler(null);
    try {
      if (!DEMO) await api.del(`/mietverhaeltnisse/${loeschen.id}`);
      const id = loeschen.id;
      onVorschau((a) => ({ ...a, mietverhaeltnisse: a.mietverhaeltnisse.filter((m) => m.id !== id) }));
      setMeldung(`Das Mietverhältnis mit ${loeschen.mieterName} wurde gelöscht.`);
      setLoeschen(null);
      setPanel(null);
      onGeaendert();
    } catch (err) {
      setLoeschFehler(err instanceof ApiFehler ? err.message : 'Das Mietverhältnis konnte nicht gelöscht werden.');
    } finally {
      setLoeschtGerade(false);
    }
  }

  const loeschDialog = (
    <Bestaetigung
      offen={loeschen !== null}
      titel="Mietverhältnis löschen?"
      aktion="Mietverhältnis löschen"
      ladend={loeschtGerade}
      fehler={loeschFehler}
      onBestaetigen={() => void mvLoeschen()}
      onAbbrechen={() => setLoeschen(null)}
    >
      {loeschen && (
        <>
          <p>
            Das Mietverhältnis mit <span className="font-medium text-fg">{loeschen.mieterName}</span> ab{' '}
            {formatDatum(loeschen.beginn)} wird mit Vertragsdaten und Mieterkontakt gelöscht.
          </p>
          <p>
            Nur für versehentlich oder fehlerhaft erfasste Mietverhältnisse. Zieht ein Mieter aus, erfassen Sie den Auszug —
            das Mietverhältnis bleibt dann in der Historie und in den Abrechnungen erhalten.
          </p>
        </>
      )}
    </Bestaetigung>
  );

  useEffect(() => {
    if (!neuOffen || DEMO) return;
    api
      .get<{ mieter: MieterV[] }>('/mieter')
      .then((d) => setMieter(d.mieter.map((m) => ({ id: m.id, name: m.name }))))
      .catch(() => setMieter([]));
  }, [neuOffen]);

  function neuesMietverhaeltnis(beginn?: string) {
    setVorgabeBeginn(beginn);
    setNeuOffen(true);
    setPanel(null);
  }

  const formular = neuOffen && (
    <LeaseFormular
      einheiten={[{ id: einheit.id, bezeichnung: einheit.bezeichnung, wohnflaecheQm: einheit.wohnflaecheQm }]}
      festeEinheitId={einheit.id}
      vorgabeBeginn={vorgabeBeginn}
      titel={aktiv ? 'Nachmieter erfassen' : 'Neues Mietverhältnis'}
      mieter={mieter}
      onAngelegt={(l) => {
        if (DEMO) {
          onVorschau((a) => ({
            ...a,
            mietverhaeltnisse: [
              {
                id: l.id,
                einheitId: einheit.id,
                mieterId: `m-${l.id}`,
                mieterName: l.mieter,
                beginn: l.beginn,
                ende: l.ende,
                kaltmieteCent: l.kaltmieteCent,
                vzNebenkostenCent: l.vzNkCent,
                vzHeizkostenCent: l.vzHeizCent,
                personenzahl: l.personen,
                kautionCent: l.kautionCent,
                kautionStatus: l.kautionStatus,
                mietart: l.mietart,
                letzteErhoehungAm: l.letzteErhoehungAm,
              },
              ...a.mietverhaeltnisse,
            ],
          }));
        }
        setNeuOffen(false);
        setMeldung('Das Mietverhältnis wurde angelegt.');
        onGeaendert();
      }}
      onAbbruch={() => setNeuOffen(false)}
    />
  );

  if (!aktiv) {
    return (
      <div className="space-y-3">
        {meldung && <Erfolg text={meldung} />}
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-fg">
                {einheit.eigennutzung ? 'Eigennutzung' : 'Leerstand'}
              </p>
              <p className="mt-1 text-sm text-fg-muted">
                {einheit.eigennutzung
                  ? 'Die Einheit wird vom Eigentümer selbst genutzt. Ihr Kostenanteil wird nicht auf Mieter umgelegt.'
                  : (() => {
                      const letztes = akte.mietverhaeltnisse.find((m) => m.ende !== null && m.ende < heuteIso());
                      return letztes?.ende
                        ? `Seit ${formatDatum(tagDanach(letztes.ende))} nicht vermietet. Den Kostenanteil leerstehender Einheiten trägt der Vermieter.`
                        : 'Für diese Einheit ist noch kein Mietverhältnis erfasst.';
                    })()}
              </p>
            </div>
            {!neuOffen && (
              <Button icon={<IconPlus groesse={18} />} onClick={() => neuesMietverhaeltnis(kuenftig ? undefined : heuteIso())}>
                Mietverhältnis anlegen
              </Button>
            )}
          </div>
          {kuenftig && (
            <div className="mt-4 rounded-lg border border-border bg-surface-2 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Angekündigter Einzug</p>
                <button
                  type="button"
                  onClick={() => {
                    setLoeschFehler(null);
                    setLoeschen(kuenftig);
                  }}
                  className="rounded-md text-xs font-medium text-fg-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  Löschen
                </button>
              </div>
              <p className="mt-1 text-sm font-semibold text-fg">{kuenftig.mieterName}</p>
              <p className="text-sm text-fg-muted">
                ab {formatDatum(kuenftig.beginn)} · {kuenftig.personenzahl} Pers. · Kaltmiete {formatEuro(kuenftig.kaltmieteCent)}
              </p>
            </div>
          )}
        </Card>
        {formular}
        {loeschDialog}
      </div>
    );
  }

  const lease = alsLease(aktiv, einheit.bezeichnung);
  const jetzt = werteAm(aktiv, heuteIso());
  const warm = jetzt.kaltmieteCent + jetzt.vzNebenkostenCent + jetzt.vzHeizkostenCent;
  const naechste = (aktiv.staende ?? []).filter((x) => x.gueltigAb > heuteIso()).sort((a, b) => a.gueltigAb.localeCompare(b.gueltigAb))[0];
  const kautionStatus = kaution ?? aktiv.kautionStatus ?? 'offen';

  async function kautionAendern(s: KautionStatus) {
    setKaution(s);
    if (DEMO) return;
    try {
      await api.patch(`/mietverhaeltnisse/${aktiv!.id}`, { kautionStatus: s });
    } catch {
      setKaution(null);
    }
  }

  const aktionen: Array<{ id: Exclude<Panel, null>; label: string }> = [
    { id: 'auszug', label: aktiv.ende ? 'Auszug bearbeiten' : 'Auszug / Mieterwechsel' },
    { id: 'vertrag', label: 'Vertragsänderung' },
    { id: 'kontakt', label: 'Kontaktdaten' },
    { id: 'zahlungen', label: 'Zahlungen' },
    { id: 'erhoehung', label: 'Mieterhöhung prüfen' },
    { id: 'uebergabe', label: 'Übergabe' },
    { id: 'verlauf', label: 'Kommunikation' },
  ];

  return (
    <div className="space-y-3">
      {meldung && <Erfolg text={meldung} />}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Mieter</p>
            <p className="mt-0.5 truncate text-lg font-semibold text-fg">{aktiv.mieterName}</p>
            <p className="text-sm text-fg-muted">
              seit {formatDatum(aktiv.beginn)} · {dauer(aktiv.beginn, null)} · {jetzt.personenzahl}{' '}
              {jetzt.personenzahl === 1 ? 'Person' : 'Personen'}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge ton="info">{MIETART_LABEL[aktiv.mietart] ?? 'Normalmiete'}</Badge>
            <Badge ton={kautionTon(kautionStatus)}>{KAUTION_LABEL[kautionStatus]}</Badge>
          </div>
        </div>

        {aktiv.ende && (
          <p className="mt-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-fg">
            Auszug zum {formatDatum(aktiv.ende)} erfasst.
            {kuenftig ? ` Nachmieter ab ${formatDatum(kuenftig.beginn)}: ${kuenftig.mieterName}.` : ' Noch kein Nachmieter erfasst.'}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Kennzahl bezeichnung="Kaltmiete" wert={formatEuro(jetzt.kaltmieteCent)} />
          <Kennzahl bezeichnung="VZ Nebenkosten" wert={formatEuro(jetzt.vzNebenkostenCent)} />
          <Kennzahl bezeichnung="VZ Heizkosten" wert={formatEuro(jetzt.vzHeizkostenCent)} />
          <Kennzahl bezeichnung="Warmmiete" wert={formatEuro(warm)} zusatz="je Monat" />
        </div>
        {naechste && (
          <p className="mt-3 rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
            Ab {formatDatum(naechste.gueltigAb)}: {GRUND_LABEL[naechste.grund]} — Kaltmiete {formatEuro(naechste.kaltmieteCent)}, VZ{' '}
            {formatEuro(naechste.vzNebenkostenCent + naechste.vzHeizkostenCent)}, {naechste.personenzahl}{' '}
            {naechste.personenzahl === 1 ? 'Person' : 'Personen'}.
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-[1fr_14rem] sm:items-end">
          <p className="text-sm text-fg-muted">
            Kaution {formatEuro(aktiv.kautionCent ?? 0)}
            {objekt.mietspiegelQmCent && einheit.wohnflaecheQm > 0 ? (
              <>
                {' · '}
                {formatEuro(Math.round(jetzt.kaltmieteCent / einheit.wohnflaecheQm))}/m² (Mietspiegel{' '}
                {formatEuro(objekt.mietspiegelQmCent)}/m²)
              </>
            ) : null}
          </p>
          <label className="block">
            <span className="sr-only">Kautionsstatus</span>
            <select
              className={feldKlasse}
              value={kautionStatus}
              onChange={(e) => void kautionAendern(e.target.value as KautionStatus)}
              aria-label="Kautionsstatus"
            >
              {(Object.keys(KAUTION_LABEL) as KautionStatus[]).map((k) => (
                <option key={k} value={k}>
                  {KAUTION_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {aktionen.map((a) => (
            <Button
              key={a.id}
              variante={panel === a.id ? 'primaer' : 'sekundaer'}
              groesse="sm"
              onClick={() => setPanel((p) => (p === a.id ? null : a.id))}
              aria-expanded={panel === a.id}
            >
              {a.label}
            </Button>
          ))}
        </div>

        {panel === 'auszug' && (
          <AuszugPanel
            mv={aktiv}
            zaehler={akte.zaehler}
            onFertig={(ende, nachmieter) => {
              if (DEMO) {
                onVorschau((a) => ({
                  ...a,
                  mietverhaeltnisse: a.mietverhaeltnisse.map((m) => (m.id === aktiv.id ? { ...m, ende } : m)),
                }));
              }
              setPanel(null);
              setMeldung(`Auszug zum ${formatDatum(ende)} erfasst.`);
              onGeaendert();
              if (nachmieter) neuesMietverhaeltnis(tagDanach(ende));
            }}
          />
        )}
        {panel === 'vertrag' && (
          <VertragPanel
            mv={aktiv}
            ergebnisse={akte.abrechnungen}
            onGespeichert={(staende, text) => {
              onVorschau((a) => ({
                ...a,
                mietverhaeltnisse: a.mietverhaeltnisse.map((m) => (m.id === aktiv.id ? { ...m, staende } : m)),
              }));
              setPanel(null);
              setMeldung(text);
              if (!DEMO) onGeaendert();
            }}
          />
        )}
        {panel === 'kontakt' && (
          <MieterKontaktPanel
            mieterId={aktiv.mieterId}
            name={aktiv.mieterName}
            onGespeichert={(name) => {
              onVorschau((a) => ({
                ...a,
                mietverhaeltnisse: a.mietverhaeltnisse.map((m) => (m.mieterId === aktiv.mieterId ? { ...m, mieterName: name } : m)),
              }));
              if (!DEMO) onGeaendert();
            }}
          />
        )}
        {panel === 'erhoehung' && (
          <MieterhoehungPanel lease={lease} flaecheQm={einheit.wohnflaecheQm} mietspiegelCent={objekt.mietspiegelQmCent} />
        )}
        {panel === 'zahlungen' && <ZahlungenPanel lease={lease} />}
        {panel === 'uebergabe' && <UebergabePanel lease={lease} />}
        {panel === 'verlauf' && <KommunikationPanel lease={lease} />}

        <div className="mt-4 flex justify-end border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              setLoeschFehler(null);
              setLoeschen(aktiv);
            }}
            className="rounded-md text-xs font-medium text-fg-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            Fehlerhaft erfasst? Mietverhältnis löschen
          </button>
        </div>
      </Card>
      {formular}
      {loeschDialog}
    </div>
  );
}

function Erfolg({ text }: { text: string }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg"
      role="status"
    >
      {text}
    </motion.p>
  );
}

function letzterTagDesMonats(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}

/**
 * Auszug erfassen: Mietende setzen und die Zählerstände zum Auszug ablesen. Ohne
 * Zwischenablesung lässt sich der Verbrauch nicht tagegenau zwischen Vor- und Nachmieter
 * trennen — deshalb werden die Zähler der Einheit direkt mit abgefragt.
 */
function AuszugPanel({
  mv,
  zaehler,
  onFertig,
}: {
  mv: Mietverhaeltnis;
  zaehler: ZaehlerMitAblesungen[];
  onFertig: (ende: string, nachmieter: boolean) => void;
}) {
  const [ende, setEnde] = useState(mv.ende ?? letzterTagDesMonats(heuteIso()));
  const [staende, setStaende] = useState<Record<string, string>>({});
  const [nachmieter, setNachmieter] = useState(true);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState(false);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!ende || ende < mv.beginn) {
      setFehler('Das Mietende darf nicht vor dem Mietbeginn liegen.');
      return;
    }
    const ablesungen: Array<{ id: string; stand: number }> = [];
    for (const z of zaehler) {
      const roh = (staende[z.id] ?? '').trim();
      if (!roh) continue;
      const stand = parseZahlEingabe(roh);
      if (stand === null || stand < 0) {
        setFehler(`Zähler ${z.nummer}: Bitte einen gültigen Stand angeben.`);
        return;
      }
      ablesungen.push({ id: z.id, stand });
    }
    setSendet(true);
    try {
      if (!DEMO) {
        await api.patch(`/mietverhaeltnisse/${mv.id}`, { ende });
        for (const a of ablesungen) {
          await api.post(`/zaehler/${a.id}/ablesungen`, { datum: ende, stand: a.stand, anlass: 'auszug' });
        }
      }
      setFertig(true);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Der Auszug konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  if (fertig) {
    return (
      <div className="mt-4 border-t border-border pt-4">
        <SuccessAnimation
          titel="Auszug erfasst"
          groesse={64}
          beschreibung={`Mietende ${formatDatum(ende)}. Nebenkosten werden tagegenau abgegrenzt.`}
          onFertig={() => onFertig(ende, nachmieter)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={speichern} className="mt-4 space-y-4 border-t border-border pt-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Mietende (letzter Tag)" type="date" value={ende} onChange={(e) => setEnde(e.target.value)} />
      </div>
      {zaehler.length > 0 ? (
        <div>
          <p className="mb-1.5 text-sm font-medium text-fg">Zählerstände zum Auszug</p>
          <p className="mb-2 text-xs text-fg-muted">
            Grundlage für die verbrauchsabhängige Abgrenzung zwischen Vor- und Nachmieter. Leere Felder werden übersprungen.
          </p>
          <div className="divide-y divide-border rounded-lg border border-border">
            {zaehler.map((z) => {
              const letzte = z.ablesungen[z.ablesungen.length - 1];
              return (
                <div key={z.id} className="grid grid-cols-[1fr_8rem] items-center gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-fg">
                      {MEDIUM_META[z.medium as keyof typeof MEDIUM_META]?.label ?? z.medium} · {z.nummer}
                    </p>
                    {letzte && (
                      <p className="text-xs text-fg-subtle">
                        zuletzt {formatZahl(letzte.stand)} {z.einheit} am {formatDatum(letzte.datum)}
                      </p>
                    )}
                  </div>
                  <label className="relative block">
                    <span className="sr-only">Stand {z.nummer}</span>
                    <input
                      inputMode="decimal"
                      className={cn(feldKlasse, 'pr-12 text-right tabellenziffern')}
                      value={staende[z.id] ?? ''}
                      onChange={(e) => setStaende((s) => ({ ...s, [z.id]: e.target.value }))}
                    />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-fg-subtle">
                      {z.einheit}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-fg-muted">Für diese Einheit sind keine Zähler erfasst.</p>
      )}
      <label className="flex items-center gap-2 text-sm text-fg">
        <input type="checkbox" checked={nachmieter} onChange={(e) => setNachmieter(e.target.checked)} />
        Anschließend den Nachmieter erfassen
      </label>
      {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
      <Button type="submit" ladend={sendet}>
        Auszug speichern
      </Button>
    </form>
  );
}

/* ------------------------------------------------------------------ Mieterhistorie */

function Verlauf({ mvs, einheit }: { mvs: Mietverhaeltnis[]; einheit: Einheit }) {
  const heute = heuteIso();
  const eintraege = useMemo(() => {
    const sortiert = [...mvs].sort((a, b) => b.beginn.localeCompare(a.beginn));
    const liste: Array<{ art: 'mv'; mv: Mietverhaeltnis } | { art: 'leer'; von: string; bis: string | null }> = [];
    sortiert.forEach((m, i) => {
      const neuer = sortiert[i - 1];
      // Lücke zum nachfolgenden (neueren) Mietverhältnis bzw. bis heute.
      if (m.ende) {
        const von = tagDanach(m.ende);
        const bis = neuer ? neuer.beginn : null;
        if (!neuer && von <= heute) liste.push({ art: 'leer', von, bis: null });
        else if (neuer && bis && von < bis) liste.push({ art: 'leer', von, bis: tagDavor(bis) });
      }
      liste.push({ art: 'mv', mv: m });
    });
    return liste;
  }, [mvs, heute]);

  if (mvs.length === 0) {
    return (
      <Card>
        <p className="text-sm text-fg-muted">
          Für {einheit.bezeichnung} ist noch kein Mietverhältnis erfasst.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <ol className="relative space-y-4 pl-6">
        <span className="absolute bottom-1 left-[7px] top-1 w-px bg-border" aria-hidden="true" />
        {eintraege.map((e, i) =>
          e.art === 'leer' ? (
            <li key={`leer-${i}`} className="relative">
              <span className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-pill border-2 border-warning bg-surface" aria-hidden="true" />
              <p className="text-sm font-medium text-warning-soft-fg">Leerstand</p>
              <p className="text-xs text-fg-muted">
                {e.bis ? `${formatDatum(e.von)} – ${formatDatum(e.bis)}` : `seit ${formatDatum(e.von)}`}
              </p>
            </li>
          ) : (
            <li key={e.mv.id} className="relative">
              <span
                className={cn(
                  'absolute -left-6 top-1 h-3.5 w-3.5 rounded-pill border-2',
                  e.mv.beginn > heute
                    ? 'border-accent bg-surface'
                    : e.mv.ende === null || e.mv.ende >= heute
                      ? 'border-success bg-success'
                      : 'border-border-strong bg-surface-3',
                )}
                aria-hidden="true"
              />
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-sm font-semibold text-fg">{e.mv.mieterName}</p>
                <p className="text-xs text-fg-subtle tabellenziffern">
                  {formatDatum(e.mv.beginn)} – {e.mv.ende ? formatDatum(e.mv.ende) : 'laufend'}
                </p>
              </div>
              <p className="text-xs text-fg-muted">
                {e.mv.beginn > heute ? 'Einzug angekündigt' : dauer(e.mv.beginn, e.mv.ende && e.mv.ende < heute ? e.mv.ende : null)}
                {' · '}
                {(() => {
                  const w = werteAm(e.mv, heute);
                  return `${w.personenzahl} ${w.personenzahl === 1 ? 'Person' : 'Personen'} · Kaltmiete ${formatEuro(w.kaltmieteCent)}`;
                })()}
              </p>
              {(e.mv.staende ?? []).length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {[...(e.mv.staende ?? [])]
                    .sort((a, b) => a.gueltigAb.localeCompare(b.gueltigAb))
                    .map((st) => (
                      <li key={st.id} className="text-xs text-fg-subtle tabellenziffern">
                        ab {formatDatum(st.gueltigAb)}: {GRUND_LABEL[st.grund]} — Kaltmiete {formatEuro(st.kaltmieteCent)}, VZ{' '}
                        {formatEuro(st.vzNebenkostenCent + st.vzHeizkostenCent)}, {st.personenzahl} Pers.
                      </li>
                    ))}
                </ul>
              )}
            </li>
          ),
        )}
      </ol>
    </Card>
  );
}

/* ------------------------------------------------------------------ Verbrauch */

function Verbrauch({ akte, onGeaendert }: { akte: AkteAntwort; onGeaendert: () => void }) {
  const [neu, setNeu] = useState(false);
  const [zusaetzlich, setZusaetzlich] = useState<ZaehlerMitAblesungen[]>([]);
  const alle = [...akte.zaehler, ...zusaetzlich];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-muted">
          Verbrauch je Ableseabschnitt. Bei Mieterwechsel trennt die Ablesung zum Auszug die Verbräuche.
        </p>
        <Button variante="sekundaer" groesse="sm" icon={<IconPlus groesse={16} />} onClick={() => setNeu((n) => !n)}>
          Zähler anlegen
        </Button>
      </div>
      {neu && (
        <ZaehlerFormular
          objektId={akte.objekt.id}
          festeEinheit={{ id: akte.einheit.id, bezeichnung: akte.einheit.bezeichnung }}
          onAngelegt={(z) => {
            setNeu(false);
            if (DEMO) {
              setZusaetzlich((l) => [...l, { id: z.id, nummer: z.nummer, medium: z.medium, einheit: z.einheit, fernablesbar: z.fernablesbar, eingebautAm: null, ablesungen: [] }]);
            } else onGeaendert();
          }}
          onAbbruch={() => setNeu(false)}
        />
      )}
      {alle.length === 0 ? (
        <Card>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-fg-subtle">
              <IconZaehler groesse={20} />
            </span>
            <p className="text-sm text-fg-muted">
              Für diese Einheit sind keine Zähler erfasst. Ohne Einzelzähler werden Wasser und Heizung nach dem
              vereinbarten Ersatzschlüssel (z. B. Wohnfläche oder Personen) verteilt.
            </p>
          </div>
        </Card>
      ) : (
        alle.map((z) => <ZaehlerVerbrauch key={z.id} z={z} mvs={akte.mietverhaeltnisse} onGeaendert={onGeaendert} />)
      )}
    </div>
  );
}

function tagDavor(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function mieterIm(mvs: Mietverhaeltnis[], von: string, bis: string): string | null {
  const mitte = new Date((Date.parse(von) + Date.parse(bis)) / 2).toISOString().slice(0, 10);
  return mvs.find((m) => m.beginn <= mitte && (m.ende === null || m.ende >= mitte))?.mieterName ?? null;
}

function ZaehlerVerbrauch({
  z,
  mvs,
  onGeaendert,
}: {
  z: ZaehlerMitAblesungen;
  mvs: Mietverhaeltnis[];
  onGeaendert: () => void;
}) {
  const reduziert = useReducedMotion();
  const [ablesungen, setAblesungen] = useState(z.ablesungen);
  const [offen, setOffen] = useState(false);
  const zeitraeume = verbrauchszeitraeume(ablesungen);
  const maxJeTag = Math.max(0.0001, ...zeitraeume.map((p) => p.jeTag));
  const letzte = ablesungen[ablesungen.length - 1];
  const label = MEDIUM_META[z.medium as keyof typeof MEDIUM_META]?.label ?? z.medium;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{label}</span>
            <Badge ton="neutral">{z.nummer}</Badge>
            {z.fernablesbar && <Badge ton="info">fernablesbar</Badge>}
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {letzte
              ? `Letzter Stand ${formatZahl(letzte.stand)} ${z.einheit} am ${formatDatum(letzte.datum)} (${ANLASS_LABEL[letzte.anlass] ?? letzte.anlass})`
              : 'Noch keine Ablesung erfasst'}
          </p>
        </div>
        <Button variante="sekundaer" groesse="sm" onClick={() => setOffen((o) => !o)}>
          Ablesung erfassen
        </Button>
      </div>
      {offen && (
        <AblesungFormular
          zaehler={{ id: z.id, einheit: z.einheit }}
          onGespeichert={(stand, datum, anlass) => {
            setAblesungen((l) => [...l, { datum, stand, anlass }].sort((a, b) => a.datum.localeCompare(b.datum)));
            setOffen(false);
            if (!DEMO) onGeaendert();
          }}
        />
      )}
      {zeitraeume.length > 0 && (
        <ul className="mt-4 space-y-2.5">
          {zeitraeume.map((p, i) => {
            const mieter = mieterIm(mvs, p.von, p.bis);
            return (
              <li key={`${p.von}-${p.bis}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs">
                  <span className="text-fg-muted tabellenziffern">
                    {formatZeitraum(p.von, p.bis)}
                    {mieter ? <span className="text-fg-subtle"> · {mieter}</span> : <span className="text-warning-soft-fg"> · Leerstand</span>}
                  </span>
                  <span className="font-semibold text-fg tabellenziffern">
                    {formatZahl(p.verbrauch)} {z.einheit}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-pill bg-surface-3">
                  <motion.div
                    className="h-full rounded-pill bg-accent"
                    initial={reduziert ? false : { width: 0 }}
                    animate={{ width: `${Math.max(2, (p.jeTag / maxJeTag) * 100)}%` }}
                    transition={{ duration: 0.5, delay: reduziert ? 0 : i * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
                  />
                </div>
                <p className="mt-0.5 text-[11px] text-fg-subtle tabellenziffern">
                  {formatZahl(Math.round(p.jeTag * 100) / 100)} {z.einheit} je Tag · {p.tage} Tage
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ Abrechnungen */

const STATUS_LABEL: Record<string, string> = {
  offen: 'Entwurf',
  berechnet: 'Berechnet',
  festgeschrieben: 'Festgeschrieben',
  versendet: 'Versendet',
};

function Abrechnungen({ akte, onOeffneAbrechnungen }: { akte: AkteAntwort; onOeffneAbrechnungen?: () => void }) {
  const zeitraeume = Array.from(
    new Map(
      [...akte.abrechnungen, ...akte.leerstandskosten].map((a) => [a.abrechnungId, { von: a.zeitraumVon, bis: a.zeitraumBis }]),
    ).entries(),
  ).sort((a, b) => b[1].bis.localeCompare(a[1].bis));

  if (zeitraeume.length === 0) {
    return (
      <Card>
        <p className="text-sm text-fg-muted">
          Für diese Einheit liegt noch keine Abrechnung vor. Abrechnungen werden je Objekt und Zeitraum erstellt.
        </p>
        {onOeffneAbrechnungen && (
          <Button className="mt-3" variante="sekundaer" groesse="sm" onClick={onOeffneAbrechnungen}>
            Zu den Abrechnungen
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {zeitraeume.map(([id, z]) => {
        const ergebnisse = akte.abrechnungen.filter((a) => a.abrechnungId === id);
        const leer = akte.leerstandskosten.find((l) => l.abrechnungId === id);
        const status = ergebnisse[0]?.status;
        return (
          <Card key={id}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-fg">Abrechnungszeitraum {formatZeitraum(z.von, z.bis)}</p>
              {status && <Badge ton={status === 'versendet' ? 'erfolg' : 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>}
            </div>
            <div className="divide-y divide-border">
              {ergebnisse.map((e) => {
                const s = beschreibeSaldo(e.saldoCent);
                return (
                  <div key={e.mietverhaeltnisId} className="grid grid-cols-2 gap-3 py-2.5 sm:grid-cols-[1fr_8rem_8rem_9rem]">
                    <p className="col-span-2 truncate text-sm font-medium text-fg sm:col-span-1">{e.mieterName}</p>
                    <Wert label="Kostenanteil" wert={formatEuro(e.kostenCent)} />
                    <Wert label="Vorauszahlungen" wert={formatEuro(e.vorauszahlungCent)} />
                    <Wert
                      label={s.bezeichnung}
                      wert={s.betrag}
                      klasse={s.ton === 'warnung' ? 'text-warning-soft-fg' : s.ton === 'erfolg' ? 'text-success' : 'text-fg'}
                    />
                  </div>
                );
              })}
              {leer && (
                <div className="grid grid-cols-2 gap-3 py-2.5 sm:grid-cols-[1fr_8rem_8rem_9rem]">
                  <p className="col-span-2 text-sm text-fg-muted sm:col-span-1">Leerstand (trägt der Vermieter)</p>
                  <Wert label="Kostenanteil" wert={formatEuro(leer.kostenCent)} />
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function Wert({ label, wert, klasse }: { label: string; wert: ReactNode; klasse?: string }) {
  return (
    <div className="min-w-0 sm:text-right">
      <p className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className={cn('text-sm font-semibold tabellenziffern', klasse ?? 'text-fg')}>{wert}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ Einheit bearbeiten */

const LAGEN = ['links', 'mitte links', 'mitte', 'mitte rechts', 'rechts'];

function EinheitBearbeiten({
  einheit,
  struktur,
  hatMietverhaeltnisse,
  onGespeichert,
  onGeloescht,
}: {
  einheit: Einheit;
  struktur: AkteAntwort['objekt'];
  hatMietverhaeltnisse: boolean;
  onGespeichert: (e: Einheit) => void;
  onGeloescht: () => void;
}) {
  const [loeschen, setLoeschen] = useState(false);
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  const [loeschtGerade, setLoeschtGerade] = useState(false);

  async function einheitLoeschen() {
    setLoeschtGerade(true);
    setLoeschFehler(null);
    try {
      if (DEMO) {
        const h = demoHaus(einheit.objektId);
        if (h) {
          const rest = h.einheiten.filter((x) => x.id !== einheit.id);
          demoHausSpeichern({ ...h, einheiten: rest, kennzahlen: kennzahlenAus(rest) });
        }
      } else {
        await api.del(`/einheiten/${einheit.id}`);
      }
      setLoeschen(false);
      onGeloescht();
    } catch (err) {
      setLoeschFehler(err instanceof ApiFehler ? err.message : 'Die Einheit konnte nicht gelöscht werden.');
    } finally {
      setLoeschtGerade(false);
    }
  }

  const [bezeichnung, setBezeichnung] = useState(einheit.bezeichnung);
  const [flaeche, setFlaeche] = useState(formatZahl(einheit.wohnflaecheQm));
  const [zimmer, setZimmer] = useState(einheit.zimmer === null ? '' : formatZahl(einheit.zimmer));
  const [geschoss, setGeschoss] = useState<string>(einheit.geschoss === null ? '' : String(einheit.geschoss));
  const [position, setPosition] = useState(einheit.position ?? '');
  const [nutzung, setNutzung] = useState(einheit.nutzungsart);
  const [eigen, setEigen] = useState(einheit.eigennutzung);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const liste = etagen(struktur.obergeschosse === null ? { ...struktur, obergeschosse: Math.max(0, einheit.geschoss ?? 0) } : struktur);
  if (einheit.geschoss !== null && !liste.includes(einheit.geschoss) && einheit.geschoss !== DACHGESCHOSS) liste.push(einheit.geschoss);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const qm = parseZahlEingabe(flaeche) ?? 0;
    const zi = zimmer.trim() ? parseZahlEingabe(zimmer) : null;
    if (!bezeichnung.trim() || !(qm > 0)) {
      setFehler('Bitte Bezeichnung und eine Wohnfläche größer null angeben.');
      return;
    }
    const daten = {
      bezeichnung: bezeichnung.trim(),
      wohnflaecheQm: qm,
      zimmer: zi,
      geschoss: geschoss === '' ? null : Number(geschoss),
      position: position || null,
      reihenfolge: position ? LAGEN.indexOf(position) + 1 : 0,
      nutzungsart: nutzung,
      eigennutzung: eigen,
    };
    setSendet(true);
    try {
      const neu = DEMO
        ? { ...einheit, ...daten }
        : (await api.patch<{ einheit: Einheit }>(`/einheiten/${einheit.id}`, daten)).einheit;
      onGespeichert(neu);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Einheit konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Einheit bearbeiten" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Bezeichnung" value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
          <Input
            label="Wohnfläche (m²)"
            inputMode="decimal"
            value={flaeche}
            onChange={(e) => setFlaeche(e.target.value)}
            hinweis="Umlageschlüssel für flächenbezogene Kosten."
          />
          <Input label="Zimmer" inputMode="decimal" value={zimmer} onChange={(e) => setZimmer(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Etage</span>
            <select className={feldKlasse} value={geschoss} onChange={(e) => setGeschoss(e.target.value)}>
              <option value="">Ohne Etage</option>
              {liste.map((g) => (
                <option key={g} value={g}>
                  {geschossLang(g)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Lage auf der Etage</span>
            <select className={feldKlasse} value={position} onChange={(e) => setPosition(e.target.value)}>
              <option value="">—</option>
              {LAGEN.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Nutzung</span>
            <select className={feldKlasse} value={nutzung} onChange={(e) => setNutzung(e.target.value as 'wohnen' | 'gewerbe')}>
              <option value="wohnen">Wohnen</option>
              <option value="gewerbe">Gewerbe</option>
            </select>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={eigen} onChange={(e) => setEigen(e.target.checked)} />
          Eigennutzung durch den Eigentümer
        </label>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button type="submit" ladend={sendet}>
            Speichern
          </Button>
          <Button
            type="button"
            variante="gefahr"
            groesse="sm"
            onClick={() => {
              setLoeschFehler(null);
              setLoeschen(true);
            }}
          >
            Einheit löschen
          </Button>
        </div>
      </form>
      <Bestaetigung
        offen={loeschen}
        titel={`${einheit.bezeichnung} löschen?`}
        aktion="Einheit löschen"
        ladend={loeschtGerade}
        gesperrt={hatMietverhaeltnisse}
        fehler={
          loeschFehler ??
          (hatMietverhaeltnisse
            ? 'Zu dieser Einheit sind Mietverhältnisse erfasst. Eine vermietete oder früher vermietete Einheit bleibt für Historie und Abrechnungen bestehen. Versehentlich erfasste Mietverhältnisse lassen sich im Reiter „Mietverhältnis“ löschen.'
            : null)
        }
        onBestaetigen={() => void einheitLoeschen()}
        onAbbrechen={() => setLoeschen(false)}
      >
        {!hatMietverhaeltnisse && (
          <>
            <p>Die Einheit wird mit ihren Zählern und Ablesungen aus dem Haus entfernt.</p>
            <p>Einheiten, die bereits abgerechnet wurden, bleiben wegen der Aufbewahrungspflicht erhalten.</p>
          </>
        )}
      </Bestaetigung>
    </Card>
  );
}

export default EinheitAkte;
